// Turns a failed PATCH /app/save into an explicit message for the user:
// what happened, whether their data is safe, and what to do next.
import { HttpError, isUnreachableError } from '@/services/api'
import { toast } from '@/lib/toast'

export type SaveSource = 'manual' | 'autosave' | 'reconnect'

export type SaveFailureContext = {
	submit: boolean
	source: SaveSource
}

const CONTACT_HINT = 'If it keeps happening, contact us using the link at the bottom of the page.'

function errorCode(e: unknown): string | undefined {
	if (!(e instanceof HttpError)) return undefined
	const body = e.body
	if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
		return (body as { error: string }).error
	}
	return undefined
}

/**
 * Show the right toast for a failed save/submit. Returns the toast key, or null
 * when nothing was shown (e.g. the connectivity toast already covers it, or a
 * repeat autosave failure was suppressed).
 */
export function notifySaveFailure(e: unknown, ctx: SaveFailureContext): string | null {
	// The API never answered. The sticky connectivity toast is already telling
	// the user we are offline and retrying; only a submit needs its own, explicit
	// "nothing was sent" message because the user expects a definite outcome.
	if (isUnreachableError(e)) {
		if (!ctx.submit) return null
		return toast.error({
			title: 'Application not submitted',
			description:
				"We couldn't reach our servers, so nothing was sent. Your answers are kept in this tab. Please try again once the connection is back.",
		})
	}

	if (e instanceof HttpError) {
		// Handled by the api layer (redirect to login); do not stack a toast on top.
		if (e.status === 401) return null

		switch (errorCode(e)) {
			case 'submit_sync_failed':
				return toast.error({
					title: 'Application not submitted',
					description: `We couldn't sync your application with our admissions system. Your answers were saved and nothing was lost. Please try again in a few moments. ${CONTACT_HINT}`,
				})
			case 'incomplete_application':
				return toast.warning({
					title: 'Application not submitted',
					description: 'Please complete all four steps before submitting.',
				})
			case 'applications_closed':
				return toast.warning({
					title: 'Applications are closed',
					description: 'This application period has ended, so changes can no longer be saved.',
				})
			case 'invalid_payload':
				return toast.error({
					title: ctx.submit ? 'Application not submitted' : "Couldn't save your changes",
					description: `Some answers could not be processed. Please refresh the page and try again. ${CONTACT_HINT}`,
				})
			default:
				break
		}

		if (e.status === 429) {
			return toast.warning({
				title: 'Too many requests',
				description: 'Please wait a moment and try again.',
			})
		}
	}

	// Anything else: a 5xx from our API or an unexpected response shape.
	const input = {
		title: ctx.submit ? 'Application not submitted' : "Couldn't save your changes",
		description: `Something went wrong on our side. Your answers are kept in this tab. Please try again in a moment. ${CONTACT_HINT}`,
	}
	// Autosave runs every 30s; don't stack a toast per failed attempt.
	if (ctx.source === 'autosave') return toast.errorOnce('autosave-failed', input)
	return toast.error(input)
}
